import React, {createContext, useCallback, useContext, useState} from "react";
import {Classroom} from "../types";

interface PendingClassroomSelection {
    id: string;
    name?: string;
}

interface PendingSubjectSelection {
    classroomId: string;
    subjectId: string;
    name?: string;
}

interface PendingStudentEnrollment {
    classroomId: string;
    termId: string;
}

interface ClassroomContextType {
    classrooms: Classroom[];
    setClassrooms: React.Dispatch<React.SetStateAction<Classroom[]>>;
    updateClassroom: (updatedClassroom: Classroom) => void;
    addNewClassroom: (newClassroom: Classroom) => void;
    classroomVersion: number;
    pendingClassroomSelection: PendingClassroomSelection | null;
    setPendingClassroomSelection: (value: PendingClassroomSelection | null) => void;
    consumePendingClassroomSelection: () => void;
    pendingSubjectSelection: PendingSubjectSelection | null;
    setPendingSubjectSelection: (value: PendingSubjectSelection | null) => void;
    consumePendingSubjectSelection: () => void;
    pendingStudentEnrollment: PendingStudentEnrollment | null;
    setPendingStudentEnrollment: (value: PendingStudentEnrollment | null) => void;
    consumePendingStudentEnrollment: () => void;
}

export const ClassroomContext = createContext<ClassroomContextType | null>(null);

const normalizeClassroom = (classroom: Classroom): Classroom => ({
    ...classroom,
    id: classroom.id ?? classroom.uuid,
    uuid: classroom.uuid ?? classroom.id,
    subjects: classroom.subjects ?? [],
    students: classroom.students ?? [],
});

export const ClassroomContextProvider = ({ children }: { children: React.ReactNode }) => {
    const [classrooms, setClassrooms] = useState<Classroom[]>([]);
    const [classroomVersion, setClassroomVersion] = useState(0);
    const [pendingClassroomSelection, setPendingClassroomSelection] = useState<PendingClassroomSelection | null>(null);
    const [pendingSubjectSelection, setPendingSubjectSelection] = useState<PendingSubjectSelection | null>(null);
    const [pendingStudentEnrollment, setPendingStudentEnrollment] = useState<PendingStudentEnrollment | null>(null);

    const addNewClassroom = (newClassroom: Classroom) => {
        const normalized = normalizeClassroom(newClassroom);
        setClassrooms(prevClassrooms => [normalized, ...prevClassrooms]);
        setClassroomVersion(v => v + 1);
    };

    const updateClassroom = (updatedClassroom: Classroom) => {
        const normalized = normalizeClassroom(updatedClassroom);
        setClassrooms(prevClassrooms =>
            prevClassrooms.map(classroom =>
                classroom.id === normalized.id ? normalized : classroom
            )
        );
        setClassroomVersion(v => v + 1);
    };

    const consumePendingClassroomSelection = useCallback(() => {
        setPendingClassroomSelection(null);
    }, []);

    const consumePendingSubjectSelection = useCallback(() => {
        setPendingSubjectSelection(null);
    }, []);

    const consumePendingStudentEnrollment = useCallback(() => {
        setPendingStudentEnrollment(null);
    }, []);

    const value = {
        classrooms,
        setClassrooms,
        updateClassroom,
        addNewClassroom,
        classroomVersion,
        pendingClassroomSelection,
        setPendingClassroomSelection,
        consumePendingClassroomSelection,
        pendingSubjectSelection,
        setPendingSubjectSelection,
        consumePendingSubjectSelection,
        pendingStudentEnrollment,
        setPendingStudentEnrollment,
        consumePendingStudentEnrollment,
    };

    return (
        <ClassroomContext.Provider value={value}> {children} </ClassroomContext.Provider>
    );
};

export default ClassroomContext;

export const useClassroom = (): ClassroomContextType => {
    const ctx = useContext(ClassroomContext);
    if (!ctx) throw new Error('useClassroom must be used within ClassroomContextProvider');
    return ctx;
};
